import React from 'react';
import styled from 'styled-components';
import { Voice } from '../../../common/src/model.ts';
import { OctaveShift, SypMusicFile, SypNote, SypStep } from '../../../common/src/syp_file.ts';
import { ensureUnreachable } from '../../../common/src/util.ts';

interface Coordinates {
  x: number;
  y: number;
}

const lineThickness = 6;
const clefHeight = 350;
const verticalNoteSpacing = clefHeight / 8 - lineThickness / 8;
const horizontalNoteSpacing = verticalNoteSpacing * 5;

enum ClefType {
  Treble = 'treble',
  Bass = 'bass',
}

enum NoteType {
  Whole = 1,
  Half = 2,
  Quarter = 4,
  Eighth = 8,
  Sixteenth = 16,
}

export function Solfege({ sypFile }: { sypFile: SypMusicFile }) {
  const width = 6000;
  const height = 1500;
  const clefCenterY = 500;
  return (
    <SolfegeWrapper>
      <SVGWrapper viewBox={`0 0 ${width} ${height}`} xmlns="http://www.w3.org/2000/svg">
        <text x={1450} y={200} style={{ font: '200px Jost-SemiBold' }}>
          {sypFile.metadata.title}
        </text>
        <Clef left={0} right={width} centerY={clefCenterY} />
        <RenderSypFileNotes sypFile={sypFile} left={horizontalNoteSpacing} clefCenterY={clefCenterY} />
      </SVGWrapper>
    </SolfegeWrapper>
  );
}

const SolfegeWrapper = styled.div`
  fill: var(--color-text);
  stroke: var(--color-text);
  background: var(--color-background);
`;

const SVGWrapper = styled.svg`
  width: 1500px;
`;

function RenderSypFileNotes({
  sypFile,
  left,
  clefCenterY,
}: {
  sypFile: SypMusicFile;
  left: number;
  clefCenterY: number;
}) {
  let index = 0;
  return (
    <>
      {sypFile.measures.map((measure) =>
        measure.soprano.map((note) => {
          const x = left + horizontalNoteSpacing * index++;
          // TODO(hewitt): using the index for a key is not a good idea
          return (
            <React.Fragment key={index}>
              <Note
                type={noteTypeFromSypNote(note)}
                center={{ x, y: getVerticalNoteLocation(note, Voice.Soprano, ClefType.Treble, clefCenterY) }}
              />
              <SyllableName
                center={{ x, y: clefCenterY + verticalNoteSpacing * 8 }}
                sypKey={sypFile.metadata.key}
                sypNote={note}
              />
            </React.Fragment>
          );
        }),
      )}
    </>
  );
}

function noteTypeFromSypNote(sypNote: SypNote): NoteType {
  const { denominator } = sypNote;
  if (denominator < 2) {
    return NoteType.Whole;
  }
  if (denominator < 4) {
    return NoteType.Half;
  }
  if (denominator < 8) {
    return NoteType.Quarter;
  }
  if (denominator < 16) {
    return NoteType.Eighth;
  }
  return NoteType.Sixteenth;
}

function getVerticalNoteLocation(sypNote: SypNote, voice: Voice, clefType: ClefType, clefCenterY: number): number {
  if (clefType === ClefType.Bass) {
    throw new Error('Bass clef not implemented');
  }
  if (!sypNote.step) {
    throw new Error(`Expected syp step in voice ${voice} for note ${sypNote}`);
  }
  const aNoteVerticalLocation = clefCenterY + verticalNoteSpacing;
  const verticalLocation =
    getSypStepOffset(sypNote.step) +
    getVoiceOffsetForSypStep(voice, sypNote.step) +
    getOctaveOffsetForSypNote(sypNote.octaveShift);
  return aNoteVerticalLocation - verticalLocation * verticalNoteSpacing;
}

// soprano E4 to D5
// alto    A3 to G4
// tenor   E3 to D4
// bass    A2 to G3
function getVoiceOffsetForSypStep(voice: Voice, sypStep: SypStep) {
  switch (voice) {
    case Voice.Soprano:
      return sypStep > SypStep.D ? -7 : 0;
    case Voice.Alto:
      return 0;
    case Voice.Tenor:
      return 0;
    case Voice.Bass:
      return 0;
    default:
    case Voice.Piano:
      return 0;
  }
}

function getOctaveOffsetForSypNote(octaveShift?: OctaveShift): number {
  switch (octaveShift) {
    case undefined:
      return 0;
    case OctaveShift.DoubleUp:
      return 14; // TODO(hewitt): is this correct?
    case OctaveShift.Up:
      return 7;
    case OctaveShift.Down:
      return -7;
    case OctaveShift.DoubleDown:
      return -14; // TODO(hewitt): is this correct?
    default:
      ensureUnreachable(octaveShift);
  }
  return 0;
}

function getSypStepOffset(sypStep: SypStep) {
  switch (sypStep) {
    case SypStep.A:
      return 0;
    case SypStep.B:
      return 1;
    case SypStep.C:
      return 2;
    case SypStep.D:
      return 3;
    case SypStep.E:
      return 4;
    case SypStep.F:
      return 5;
    case SypStep.G:
      return 6;
    default:
      ensureUnreachable(sypStep);
  }
}

function Clef({ left, right, centerY }: { left: number; right: number; centerY: number }) {
  return (
    <svg
      x={left}
      y={centerY - clefHeight / 2}
      width={right - left}
      height={clefHeight}
      xmlns="http://www.w3.org/2000/svg"
    >
      <line x1={left} y1={lineThickness / 2} x2={right} y2={lineThickness / 2} strokeWidth={lineThickness} />
      <line
        x1={left}
        y1={clefHeight / 4 + lineThickness / 4}
        x2={right}
        y2={clefHeight / 4 + lineThickness / 4}
        strokeWidth={lineThickness}
      />
      <line x1={left} y1={clefHeight / 2} x2={right} y2={clefHeight / 2} strokeWidth={lineThickness} />
      <line
        x1={left}
        y1={(clefHeight * 3) / 4 - lineThickness / 4}
        x2={right}
        y2={(clefHeight * 3) / 4 - lineThickness / 4}
        strokeWidth={lineThickness}
      />
      <line
        x1={left}
        y1={clefHeight - lineThickness / 2}
        x2={right}
        y2={clefHeight - lineThickness / 2}
        strokeWidth={lineThickness}
      />
    </svg>
  );
}

function Note({ center, type }: { center: Coordinates; type: NoteType }) {
  const elipseCenter = { x: 50, y: 275 };
  const rx = elipseCenter.x;
  const ry = (rx * (type === NoteType.Whole ? 3.3 : 3.5)) / 5;
  const lineX = elipseCenter.x * 2 - lineThickness - (type === NoteType.Half ? 3.5 : 2.35);
  const hasTail = type === NoteType.Eighth || type === NoteType.Sixteenth;
  return (
    <svg
      x={center.x - elipseCenter.x}
      y={center.y - elipseCenter.y}
      width={elipseCenter.x * 2 + (hasTail ? elipseCenter.x * 2 : 0)}
      height="320"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <mask id={`mask-${type.toString()}`}>
          <ellipse cx={elipseCenter.x} cy={elipseCenter.y} rx={rx} ry={ry} fill="white" />
          <ellipse
            cx={elipseCenter.x}
            cy={elipseCenter.y}
            rx={type === NoteType.Whole ? rx / 1.8 : (rx - lineThickness / 2) / 1.1}
            ry={type === NoteType.Whole ? ry / 1.5 : ry / 2 / 1.2}
            fill="black"
            transform={type === NoteType.Whole ? `rotate(65, ${elipseCenter.x}, ${elipseCenter.y})` : ''}
          />
        </mask>
      </defs>
      <ellipse
        cx={elipseCenter.x}
        cy={elipseCenter.y}
        rx={rx}
        ry={ry}
        transform={type !== NoteType.Whole ? `rotate(-40, ${elipseCenter.x}, ${elipseCenter.y})` : ''}
        mask={type === NoteType.Whole || type === NoteType.Half ? `url(#mask-${type.toString()})` : ''}
      />
      {type !== NoteType.Whole && <line x1={lineX} y1="260" x2={lineX} y2="0" strokeWidth={lineThickness} />}
      {hasTail && <Tail left={lineX} top={0} />}
      {type === NoteType.Sixteenth && <Tail left={lineX} top={60} />}
    </svg>
  );
}

function Tail({ left, top }: { left: number; top: number }) {
  return (
    <svg x={left} y={top} width={65} height={clefHeight} xmlns="http://www.w3.org/2000/svg">
      <path
        fill="black"
        d={`
            m 0 75
            c0,-15.5 11.5,-5 17,-2.5
            c5.5,2.5 21,22 32,43
            c11,21 9.5,33.5 4.5,61.5
            c-5,28 20,-35 8,-65
            c-12,-30 -38.5,-58 -46.5,-67
            c-8,-9 -17,-37 -17,-47
            c0,-10 2,92.5 2,77
            z`}
      />
    </svg>
  );
}

function SyllableName({
  center,
  sypKey,
  sypNote,
}: {
  center: Coordinates;
  sypKey: string | undefined;
  sypNote: SypNote;
}) {
  return (
    <svg x={center.x - 40} y={center.y} width={150} height={100} xmlns="http://www.w3.org/2000/svg">
      <text x={0} y={80} style={{ font: '100px Jost-Regular' }}>
        {getSyllableName(sypKey, sypNote)}
      </text>
    </svg>
  );
}

function getSyllableName(sypKey: string | undefined, sypNote: SypNote): string {
  if (sypNote.isRest) {
    return '';
  }
  // TODO(hewitt): Handle keys other than G, also handle accidentals
  const { step } = sypNote;
  if (!step) {
    throw new Error(`Non rests are required to have a step`);
  }
  switch (step) {
    case SypStep.C:
      return 'fa';
    case SypStep.D:
      return 'so';
    case SypStep.E:
      return 'la';
    case SypStep.F:
      return 'ti';
    case SypStep.G:
      return 'do';
    case SypStep.A:
      return 're';
    case SypStep.B:
      return 'mi';
    default:
      ensureUnreachable(step);
  }
}
